import React, { useEffect, useRef, useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { Cascader, Input, Switch, Select, Button, Form, Divider, Tag, TimePicker } from "antd";
import dayjs from "dayjs";
import {
  add_process_data_v1,
  edit_process_data_v1,
  get_role,
  get_werehouse_area,
  get_process_data_for_process_id,
  get_process_data_v1,
} from "./mbmr_api";
import { get_api_function, post_api_function, get_process_type_list } from '@/utils/api';
import { useParams } from "react-router";
import { toast } from "react-toastify";
import { toggleRefresh } from '@/store/reducers/recipeConfigRefresh';
import { useDispatch, useSelector } from "react-redux";
import { useTheme } from '@/App';
import { getAccentColor, defaultColors } from '@/themes/antd-theme';
import { Plus, Copy, Library } from "lucide-react";
import useAppSetting from '@/hooks/useAppSetting';

// 2026-09-02, "move this dispensing toggle to under process types, selecting that type will
// set this" — the switch now lives directly under the Process Type field (same Form.Item) and
// picking a standard, seeded type (djangoapp/management/commands/init_process_types.py:
// Dispensing/Manufacturing/Filling/Packaging) sets it for you (Dispensing → Yes, the other three
// → No); a custom (admin-added, non-standard-named) type — or no type picked at all — carries no
// such signal, so it's left to the operation-type derivation (edit mode) or manual override.
const STANDARD_PROCESS_TYPE_NAMES = new Set(['dispensing', 'manufacturing', 'filling', 'packaging']);

const ProcessForm = ({ onCancel, editProcess, libraryMode = false, onLibrarySuccess }) => {
  const isEditMode = !!editProcess;
  const { isDark } = useTheme();
  // Hold Min/Max Time fields are shown only when the "Hold Time" application
  // setting is enabled.
  const holdTimeEnabled = useAppSetting('Hold Time');
  const { theme_color } = useSelector((state) => state.company_profile);
  const buttonBgColor = getAccentColor(theme_color, isDark);
  const [area_list, set_area_list] = useState([]);
  const [role_list, set_role_list] = useState([]);
  // Dispensing/Manufacturing/Filling/Packaging (or any custom process type an
  // admin has added via Custom Process) — layers additional locked-field
  // rules on top of whatever Operation Types the process's operations use.
  const [process_type_list, set_process_type_list] = useState([]);
  const [mode, setMode] = useState("add");
  // "copy" — another MBMR's process; "library" — a Process Library process (2026-10-28, owner). Both are
  // copied whole into the new process (copy_source_process_id + copy_operations).
  const isCopyMode = mode === "copy" || mode === "library";
  const [processOptions, setProcessOptions] = useState([]);
  const [copySourceProcessId, setCopySourceProcessId] = useState(null);
  // 2026-10-22 (owner) · "Copy Process" in a record copies from the same product's OTHER records: picked
  // record → process (services/recipe/process_copy_sources.py). The record's own processes are not offered.
  const [copySources, setCopySources] = useState([]);
  const [copySourcePath, setCopySourcePath] = useState([]);
  // What the operation types say this process is. The is_dispensing switch defaults to
  // it and is only an override — see djangoapp/services/recipe/dispensing_derivation.py.
  const [derivedDispensing, setDerivedDispensing] = useState(false);
  // In library mode there is no recipe in the URL — library processes are
  // recipe-less. Guard every recipe-scoped call on `!libraryMode`.
  const { recipe_id } = useParams();
  const newProcessData = useSelector((state) => state.newProcessData);
  const dispatch = useDispatch();
  const formikRef = useRef(null);
  const initialValues = {
    recipe_id: recipe_id,
    process_id: editProcess?.process_id || null,
    process_name: "",
    // Shown once to each user, before they execute this process.
    instruction: "",
    approval_roles_id: [],
    is_dispensing: false,
    area_section_id: "",
    process_type_id: null,
    manufacturing_class: "",
    roles_id: "",
    // Process-level hold time. Sent as HH:MM:SS; default "00:00:00" when not set.
    hold_min_time: "00:00:00",
    hold_max_time: "00:00:00",
    copy_source_process_id: null,
    copy_operations: false,
    // Reference to a Process Library process (recipe mode only). Sent to the
    // backend as process_reference (library payload; the recipe form no longer offers "Reference Process").
    process_reference: null,
  };

  const validationSchema = Yup.object({
    process_name: Yup.string().required("Process Name is required"),
    // 2026-10-07 · no Roles here: who performs the work is chosen on each step ("Performed by"),
    // and where, on the step too ("May run in"). The process no longer asks for either.
  });

  const success_get_werehouse_area = (res) => {
    if (res.data.status === "success") {
      set_area_list(res.data.data);
    }
  };

  const success_get_role = (res) => {
    if (res.data.status === "success") {
      set_role_list(res.data.data);
    }
  };

  useEffect(() => {
    get_werehouse_area(success_get_werehouse_area);
    get_role(success_get_role);
    get_process_type_list((res) => {
      if (res?.data?.status === "success") set_process_type_list(res?.data?.data || []);
    });
    // The Process Library (recipe-less, reusable processes): a library's copy source, and a record's
    // "Process Library" option (2026-10-28, owner).
    get_api_function('/list_library_processes', (res) => {
      const rows = res?.data?.status === 'success' ? (res?.data?.data || []) : [];
      setProcessOptions(rows.map((p) => ({ process_id: p.process_id, process_name: p.process_name })));
    }, {});
    if (!libraryMode && recipe_id) {
      get_api_function(`/designer/recipes/${recipe_id}/process-copy-sources/`, (res) => {
        setCopySources(res?.data?.status === "success" ? (res?.data?.data || []) : []);
      }, {});
    }
  }, []);

  // Fetch a source process's config (roles / approvals / areas / dispensing) and
  // prefill the form for "Copy Process" (the name gets " - Copy").
  const prefillFromProcess = (processId, { nameSuffix = "", keepName = false } = {}) => {
    if (!processId || !formikRef.current) return;
    get_process_data_for_process_id((res) => {
      if (res?.data?.status !== "success" || !res?.data?.data?.[0]) return;
      const source = res.data.data[0];
      const roleNameToId = new Map((role_list || []).map((r) => [String(r.role || r.role_name), r.role_id]));
      const areaNameToId = new Map((area_list || []).map((a) => [String(a.name), a.warehouse_area_id]));

      const roleIds = (source.roles || [])
        .map((name) => roleNameToId.get(String(name)))
        .filter(Boolean);
      const approvalRoleIds = (source.approvals || [])
        .map((name) => roleNameToId.get(String(name)))
        .filter(Boolean);
      const areaIds = (source.areas || [])
        .map((name) => areaNameToId.get(String(name)))
        .filter(Boolean);

      if (!keepName) {
        const base = source.process || "";
        formikRef.current.setFieldValue("process_name", base ? `${base}${nameSuffix}` : "");
      }
      formikRef.current.setFieldValue("instruction", source.instruction || "");
      formikRef.current.setFieldValue("roles_id", roleIds.join(","));
      formikRef.current.setFieldValue("approval_roles_id", approvalRoleIds);
      formikRef.current.setFieldValue("area_section_id", areaIds.join(","));
      formikRef.current.setFieldValue("is_dispensing", !!source.is_dispensing);
      setDerivedDispensing(!!source.is_dispensing_derived);
    }, { process_id: processId });
  };

  useEffect(() => {
    if (!isCopyMode || !copySourceProcessId || !formikRef.current) return;
    // Library copy: prefill just the name from the chosen library process; there
    // are no recipe-scoped roles/areas to carry over.
    if (libraryMode) {
      const src = processOptions.find((p) => p.process_id === copySourceProcessId);
      formikRef.current.setFieldValue("process_name", src?.process_name ? `${src.process_name} - Copy` : "");
      formikRef.current.setFieldValue("copy_source_process_id", copySourceProcessId);
      return;
    }
    get_process_data_for_process_id((res) => {
      if (res?.data?.status !== "success" || !res?.data?.data?.[0]) return;
      const source = res.data.data[0];
      const roleNameToId = new Map((role_list || []).map((r) => [String(r.role || r.role_name), r.role_id]));
      const areaNameToId = new Map((area_list || []).map((a) => [String(a.name), a.warehouse_area_id]));

      const roleIds = (source.roles || [])
        .map((name) => roleNameToId.get(String(name)))
        .filter(Boolean);
      const approvalRoleIds = (source.approvals || [])
        .map((name) => roleNameToId.get(String(name)))
        .filter(Boolean);
      const areaIds = (source.areas || [])
        .map((name) => areaNameToId.get(String(name)))
        .filter(Boolean);

      const nextName = source.process ? `${source.process} - Copy` : "";
      formikRef.current.setFieldValue("process_name", nextName);
      formikRef.current.setFieldValue("instruction", source.instruction || "");
      formikRef.current.setFieldValue("roles_id", roleIds.join(","));
      formikRef.current.setFieldValue("approval_roles_id", approvalRoleIds);
      formikRef.current.setFieldValue("area_section_id", areaIds.join(","));
      formikRef.current.setFieldValue("is_dispensing", !!source.is_dispensing);
      setDerivedDispensing(!!source.is_dispensing_derived);
      formikRef.current.setFieldValue("copy_source_process_id", copySourceProcessId);
    }, { process_id: copySourceProcessId });
  }, [mode, isCopyMode, copySourceProcessId, role_list, area_list]);

  // Pre-fill form when opening in edit mode
  useEffect(() => {
    if (!isEditMode || !editProcess?.process_id || !formikRef.current) return;

    // Library edit: prefill directly from the record the library page passed in.
    // Map its comma-separated area names back to warehouse area IDs.
    if (libraryMode) {
      formikRef.current.setFieldValue("process_name", editProcess.process_name || "");
      formikRef.current.setFieldValue("instruction", editProcess.instruction || "");
      formikRef.current.setFieldValue("is_dispensing", !!editProcess.is_dispensing_process);
      formikRef.current.setFieldValue("process_type_id", editProcess.process_type_id || null);
      setDerivedDispensing(!!editProcess.is_dispensing_derived);
      // area_section now stores area IDs (recipe-style). Older rows may still hold
      // names, so resolve each token by id first, then fall back to name.
      const areaIds = String(editProcess.area_section || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .map((tok) => {
          const byId = area_list.find((a) => String(a.warehouse_area_id) === tok);
          if (byId) return byId.warehouse_area_id;
          return area_list.find((a) => a.name === tok)?.warehouse_area_id;
        })
        .filter(Boolean);
      formikRef.current.setFieldValue("area_section_id", areaIds.join(","));
      // Roles + approval roles come back from _serialize (list_library_processes).
      formikRef.current.setFieldValue("roles_id", editProcess.roles_id || "");
      formikRef.current.setFieldValue("manufacturing_class", editProcess.manufacturing_class || "");
      formikRef.current.setFieldValue("approval_roles_id", editProcess.approval_roles_id || []);
      return;
    }

    get_process_data_v1((res) => {
      if (res?.data?.status !== "success" || !res?.data?.data) return;
      const source = res.data.data;
      formikRef.current.setFieldValue("process_name", source.process_name || "");
      formikRef.current.setFieldValue("instruction", source.instruction || "");
      formikRef.current.setFieldValue("roles_id", source.roles_id || "");
      formikRef.current.setFieldValue("approval_roles_id", source.approval_roles_id || []);
      formikRef.current.setFieldValue("area_section_id", source.area_section_id || "");
      formikRef.current.setFieldValue("is_dispensing", !!source.is_dispensing);
      formikRef.current.setFieldValue("process_type_id", source.process_type_id || null);
      setDerivedDispensing(!!source.is_dispensing_derived);
      formikRef.current.setFieldValue("hold_min_time", source.hold_min_time || "00:00:00");
      formikRef.current.setFieldValue("hold_max_time", source.hold_max_time || "00:00:00");
    }, { process_id: editProcess.process_id });
    // area_list included so library edit re-maps area names once areas load.
  }, [isEditMode, editProcess?.process_id, libraryMode, area_list]);

  const successadd_process_data_v1 = (res) => {
    if (res?.data?.status === "success") {
      const copiedCount = Number(res?.data?.copied_operations_count || 0);
      if (copiedCount > 0) {
        toast.success(`${res?.data?.message} (${copiedCount} operations copied)`);
      } else {
        toast.success(res?.data?.message);
      }
      onCancel();
      dispatch(toggleRefresh());
    } else {
      toast.warning(res?.data?.message);
    }
  };

  // Library processes are recipe-less: they persist via the library endpoints
  // and store area as a joined string (area_section), not recipe-scoped role /
  // approval rows. Map the shared form values onto the library payload.
  const buildLibraryPayload = (values) => {
    // Same payload shape as the recipe process (add_process_data_v1 /
    // edit_process_data_v1) — a library process is just a recipe-less version,
    // so the backend reads these identical keys. area_section_id carries the
    // area IDs; roles_id the employee-role ids; approval_roles_id the approvers.
    return {
      process_id: isEditMode ? editProcess?.process_id : null,
      process_name: values.process_name,
      instruction: values.instruction || "",
      approval_roles_id: values.approval_roles_id || [],
      is_dispensing: !!values.is_dispensing,
      process_type_id: values.process_type_id || null,
      area_section_id: values.area_section_id || "",
      manufacturing_class: values.manufacturing_class || null,
      roles_id: values.roles_id || "",
      hold_min_time: values.hold_min_time || "00:00:00",
      hold_max_time: values.hold_max_time || "00:00:00",
      copy_source_process_id: values.copy_source_process_id || null,
      copy_operations: !!values.copy_operations,
      process_reference: values.process_reference || null,
      set_to_approval: false,
      library_status: editProcess?.library_status || "DRAFT",
    };
  };

  const submitLibrary = (values) => {
    if (isCopyMode && !copySourceProcessId) {
      toast.warning("Please select process to copy");
      return;
    }
    const payload = buildLibraryPayload(values);
    const url = isEditMode ? "/update_library_process" : "/create_library_process";
    post_api_function(url, (res) => {
      if (res?.status === "success") {
        toast.success(res?.message || (isEditMode ? "Process updated" : "Process created"));
        onCancel();
        if (onLibrarySuccess) onLibrarySuccess(res?.data);
      } else {
        toast.warning(res?.message || "Save failed");
      }
    }, payload);
  };

  const handleSubmit = (values, { resetForm }) => {
    if (libraryMode) {
      submitLibrary(values);
      return;
    }
    if (isEditMode) {
      edit_process_data_v1((res) => {
        if (res?.data?.status === "success") {
          toast.success(res?.data?.message || "Process updated successfully");
          onCancel();
          dispatch(toggleRefresh());
        } else {
          toast.warning(res?.data?.message || "Unable to update process");
        }
      }, { ...values, process_id: editProcess.process_id, set_to_approval: false });
      return;
    }
    if (isCopyMode && !copySourceProcessId) {
      toast.warning("Please select process to copy");
      return;
    }
    add_process_data_v1(successadd_process_data_v1, { ...values, set_to_approval: false });
  };

  const textColor = isDark ? '#f9fafb' : '#1f2937';
  const secondaryColor = isDark ? defaultColors.palette.gray[400] : '#6b7280';
  const borderColor = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';
  const cardBg = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const copyBlockBg = isDark ? 'rgba(59,130,246,0.08)' : '#eff6ff';
  const copyBlockBorder = isDark ? '#1e40af' : '#bfdbfe';

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, handleSubmit, resetForm, errors, touched }) => {
        // Process Type "usable" whitelists — an empty list on the Process Type
        // means unrestricted, so every area/role stays offered. Operation Type
        // is enforced separately, in AddOperationConfig.js, once operations are
        // added to the process.
        const selectedProcessType = process_type_list.find(
          (pt) => pt.process_type_id === values.process_type_id
        );
        // Process Type's area whitelist isn't applied to the Area picker below —
        // it's now tree-sourced (any node, any type) rather than a flat
        // WerehouseAreaConfig list, and reconciling the two is the deferred
        // "constraint" from the 2026-09-02 change. Still applied to Roles.
        const allowedRoleIds = selectedProcessType?.role_ids?.length ? selectedProcessType.role_ids : null;
        const visibleRoleList = allowedRoleIds
          ? role_list.filter((r) => allowedRoleIds.includes(r.role_id))
          : role_list;

        // Area picker (LocationAreaTreeSelect, shared with AddMachineEnhanced.js's Area

        return (
        <Form layout="vertical" onFinish={handleSubmit}>

          {/* ── Mode selector cards ── */}
          {/* 2026-10-28 (owner) · not in the Process Library's own form: a library process is made from
              scratch there; copying FROM the library is a record's Copy Process. */}
          {!isEditMode && !libraryMode && (
            <div className="mb-3">
              <p className="text-xs font-medium mb-1.5" style={{ color: secondaryColor }}>
                CREATE PROCESS BY
              </p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'add', label: 'Add Process', icon: Plus, desc: 'Create from scratch' },
                  // 2026-10-22 (owner) · "Reference Process" removed: a process is created from scratch or as a
                  // copy (operations, steps and settings copied — the same copy a record and a new version use).
                  { value: 'copy', label: 'Copy Process', icon: Copy, desc: 'Duplicate an existing one' },
                  { value: 'library', label: 'Process Library', icon: Library, desc: 'Copy a library process' },
                ].map(({ value, label, icon: Icon, desc }) => {
                  const isSelected = mode === value;
                  return (
                    <div
                      key={value}
                      onClick={() => {
                        setMode(value);
                        setCopySourceProcessId(null);
                        setCopySourcePath([]);
                        setFieldValue("copy_source_process_id", null);
                        // 2026-10-22 (owner) · "copy ho ke create hona chaiye process": a copy always brings
                        // the operations (with their steps and settings) — no switch to forget.
                        setFieldValue("copy_operations", value === "copy" || value === "library");
                        setFieldValue("process_reference", null);
                      }}
                      className="cursor-pointer rounded-lg p-2 flex items-center gap-2 transition-all select-none"
                      style={{
                        border: `2px solid ${isSelected ? buttonBgColor : borderColor}`,
                        backgroundColor: isSelected
                          ? isDark ? 'rgba(32,68,72,0.2)' : '#f0f7f7'
                          : cardBg,
                      }}
                    >
                      <div
                        className="rounded-md p-1 flex-shrink-0"
                        style={{
                          backgroundColor: isSelected
                            ? isDark ? 'rgba(32,68,72,0.4)' : '#d0eded'
                            : isDark ? defaultColors.palette.gray[700] : '#f3f4f6',
                        }}
                      >
                        <Icon size={13} style={{ color: isSelected ? buttonBgColor : secondaryColor }} />
                      </div>
                      <div>
                        <p className="text-xs font-semibold m-0 leading-tight"
                          style={{ color: isSelected ? buttonBgColor : textColor }}>
                          {label}
                        </p>
                        <p className="text-xs m-0" style={{ color: secondaryColor, fontSize: 10 }}>
                          {desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Copy source block ── */}
          {!isEditMode && isCopyMode && (
            <div
              className="rounded-lg p-2.5 mb-3"
              style={{
                backgroundColor: copyBlockBg,
                border: `1px solid ${copyBlockBorder}`,
              }}
            >
              <div className="flex items-center gap-1.5 mb-2">
                {mode === "library" ? <Library size={12} style={{ color: '#3b82f6' }} /> : <Copy size={12} style={{ color: '#3b82f6' }} />}
                <span className="text-xs font-semibold" style={{ color: '#3b82f6' }}>{mode === "library" ? "Process Library" : "Copy Source"}</span>
              </div>
              <Form.Item
                label={<span className="text-xs font-medium" style={{ color: textColor }}>Select Process To Copy</span>}
                required
                style={{ marginBottom: 8 }}
              >
                {libraryMode || mode === "library" ? (
                  <Select
                    showSearch
                    value={copySourceProcessId}
                    placeholder="Select a Process Library process"
                    allowClear
                    optionFilterProp="label"
                    options={(processOptions || []).map((proc) => ({
                      value: proc.process_id,
                      label: proc.process_name,
                    }))}
                    onChange={(val) => {
                      const selected = val || null;
                      setCopySourceProcessId(selected);
                      setFieldValue("copy_source_process_id", selected);
                    }}
                    notFoundContent="No library processes available"
                  />
                ) : (
                  <Cascader
                    style={{ width: "100%" }}
                    value={copySourcePath}
                    placeholder="Select the MBMR, then its process"
                    allowClear
                    expandTrigger="hover"
                    showSearch={{ filter: (input, path) => path.some((o) => String(o.label).toLowerCase().includes(input.toLowerCase())) }}
                    options={copySources.map((r) => ({
                      value: `r${r.recipe_id}`,
                      label: `${r.name} · v${r.version}${r.code ? ` (${r.code})` : ""}`,
                      children: r.processes.map((pr) => ({
                        value: pr.process_id,
                        label: `${pr.process_name} · ${pr.operations} operation${pr.operations === 1 ? "" : "s"}`,
                      })),
                    }))}
                    onChange={(path) => {
                      const selected = (path && path[1]) || null;
                      setCopySourcePath(path || []);
                      setCopySourceProcessId(selected);
                      setFieldValue("copy_source_process_id", selected);
                    }}
                    notFoundContent="No other MBMR of this product has a process to copy"
                  />
                )}
              </Form.Item>
              <p className="m-0" style={{ color: secondaryColor, fontSize: 12.5 }}>
                The new process is created as a copy: every operation of the source, with its steps and settings, is copied.
              </p>
            </div>
          )}

          {!isEditMode && isCopyMode && (
            <Divider style={{ margin: '8px 0' }}>
              <span className="text-xs" style={{ color: secondaryColor }}>Process Details</span>
            </Divider>
          )}

          {/* ── Process Name ── */}
          <Form.Item
            label="Process Name"
            required
            style={{ marginBottom: 10 }}
            validateStatus={touched.process_name && errors.process_name ? "error" : ""}
            help={touched.process_name && errors.process_name}
          >
            <Input
              placeholder="Enter process name"
              value={values.process_name}
              
              onChange={(e) => setFieldValue("process_name", e.target.value)}
            />
          </Form.Item>

          {/* ── Instruction: each user reads it once, in a popup, before they execute this process ── */}
          <Form.Item style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
              <div>
                <p className="m-0" style={{ color: textColor, fontSize: 11, fontWeight: 500 }}>Instruction</p>
                <p className="m-0" style={{ color: secondaryColor, fontSize: 10 }}>
                  Optional. Each user reads it once before executing this process. Upload: .txt, up to 200 KB.
                </p>
              </div>
              <input
                id="process-instruction-upload"
                type="file"
                accept=".txt,text/plain"
                style={{ display: "none" }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (!f) return;
                  if (!/\.txt$/i.test(f.name) && f.type !== "text/plain") { toast.error("Choose a .txt file."); return; }
                  if (f.size > 200 * 1024) { toast.error("The file is larger than 200 KB."); return; }
                  const rd = new FileReader();
                  rd.onload = () => {
                    const lines = String(rd.result || "").replace(/^\uFEFF/, "").replace(/\r\n?/g, "\n")
                      .split("\n").map((l) => l.replace(/\s+$/, "")).filter((l) => l.trim());
                    if (!lines.length) { toast.error("The file has no text."); return; }
                    const cur = String(values.instruction || "").replace(/\s+$/, "");
                    setFieldValue("instruction", cur ? `${cur}\n${lines.join("\n")}` : lines.join("\n"));
                    toast.success(`${lines.length} line(s) added from ${f.name}.`);
                  };
                  rd.onerror = () => toast.error("The file could not be read.");
                  rd.readAsText(f, "utf-8");
                }}
              />
              <Button size="small" style={{ fontSize: 11 }} onClick={() => document.getElementById("process-instruction-upload")?.click()}>
                Upload .txt
              </Button>
            </div>
            <Input.TextArea
              rows={4}
              placeholder="Instructions to read before executing this process"
              value={values.instruction}
              onChange={(e) => setFieldValue("instruction", e.target.value)}
            />
          </Form.Item>

          {/* ── Manufacturing class (library processes are reused per class; empty = any) ── */}
          {libraryMode && (
            <Form.Item label="Manufacturing type" style={{ marginBottom: 10 }} help="Which kind of manufacturing this library process belongs to; leave empty for a shared process (e.g. dispensing, line clearance).">
              <Select
                allowClear
                value={values.manufacturing_class || undefined}
                placeholder="Any (shared)"
                onChange={(v) => setFieldValue("manufacturing_class", v || "")}
                options={[{ value: 'STERILE', label: 'Sterile' }, { value: 'OSD', label: 'OSD' }, { value: 'LIQUID', label: 'Liquid' }, { value: 'BIOLOGIC', label: 'Biologic' }, { value: 'TOPICAL', label: 'Topical' }, { value: 'API', label: 'API' }, { value: 'PACKAGING', label: 'Packaging' }]}
              />
            </Form.Item>
          )}
          {/* 2026-09-28 · Area is no longer asked for here.
              Where work happens is authored on the STEP ("Where it perform"), because that is
              the level at which it changes — a process moves through rooms, and naming one
              for the whole process states something the record then contradicts step by step.
              `area_section` is still on the model and still frozen at issue; nothing writes
              it from this form any more. */}

          {/* ── Process Type ── */}
          <Form.Item
            label="Process Type"
            style={{ marginBottom: 10 }}
            help="Dispensing, Manufacturing, Filling, Packaging, or any custom type — narrows Area/Roles/Operation Type to what that type allows, and locks the fields that type's rules define, on top of Operation Type rules."
          >
            <Select
              value={values.process_type_id || undefined}
              placeholder="Select process type (optional)"
              onChange={(val) => {
                const nextId = val || null;
                setFieldValue("process_type_id", nextId);
                const nextType = process_type_list.find((pt) => pt.process_type_id === nextId);
                // Dispensing/Manufacturing/Filling/Packaging reliably say whether they're
                // a dispensing process — picking one sets the switch below to match. A
                // custom type carries no such signal, so it's left as-is (manual, or
                // whatever the operation types already derived).
                const nextTypeName = (nextType?.name || '').trim().toLowerCase();
                if (nextTypeName === 'dispensing') {
                  setFieldValue("is_dispensing", true);
                  setDerivedDispensing(true);
                } else if (STANDARD_PROCESS_TYPE_NAMES.has(nextTypeName)) {
                  setFieldValue("is_dispensing", false);
                  setDerivedDispensing(false);
                }
                const nextRoleIds = nextType?.role_ids?.length ? nextType.role_ids : null;
                if (nextRoleIds) {
                  const currentRoleIds = values.roles_id ? values.roles_id.split(",").map((i) => parseInt(i)) : [];
                  const keptRoleIds = currentRoleIds.filter((id) => nextRoleIds.includes(id));
                  if (keptRoleIds.length !== currentRoleIds.length) {
                    toast.warning("Some selected roles aren't allowed for this process type and were removed");
                    setFieldValue("roles_id", keptRoleIds.join(","));
                  }
                }
              }}
              showSearch
              allowClear
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={process_type_list.map((i) => ({
                label: i.name,
                value: i.process_type_id,
              }))}
              className="w-full"
            />

            {/* Dispensing / weighing — set automatically by the Process Type picked above
                (Dispensing → Yes, Manufacturing/Filling/Packaging → No); a custom type
                carries no such signal so it falls back to whatever the operation types
                derive, or manual. The switch is always an override either way. */}
            <div
              className="flex items-center justify-between gap-3"
              style={{
                marginTop: 8,
                paddingTop: 8,
                borderTop: `1px dashed ${borderColor}`,
              }}
            >
              <div className="flex-1">
                <p className="m-0" style={{ color: textColor, fontSize: 11, fontWeight: 500 }}>Dispensing / weighing process</p>
                <p className="m-0" style={{ color: secondaryColor, fontSize: 10 }}>
                  {STANDARD_PROCESS_TYPE_NAMES.has((process_type_list.find((pt) => pt.process_type_id === values.process_type_id)?.name || '').trim().toLowerCase())
                    ? <>Set by the Process Type selected above. Change it only to override.</>
                    : isEditMode
                      ? <>Set automatically from the operation types — they say <strong>{derivedDispensing ? "Yes" : "No"}</strong>. Change it only to override.</>
                      : <>Set automatically once this process has operations, or by picking Dispensing above. Change it only to override.</>}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {!!values.is_dispensing !== derivedDispensing && (
                  <Tag color="orange" style={{ margin: 0 }}>Overridden</Tag>
                )}
                <Switch
                  checked={!!values.is_dispensing}
                  onChange={(val) => setFieldValue("is_dispensing", !!val)}
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                />
              </div>
            </div>
          </Form.Item>


          {/* ── Hold Time (min / max) — gated by the "Hold Time" app setting ── */}
          {holdTimeEnabled && (
            <div className="grid grid-cols-2 gap-3">
              <Form.Item label="Hold Min Time" style={{ marginBottom: 10 }}>
                <TimePicker
                  style={{ width: "100%" }}
                  format="HH:mm:ss"
                  value={values.hold_min_time ? dayjs(values.hold_min_time, "HH:mm:ss") : null}
                  onChange={(time, timeString) =>
                    setFieldValue("hold_min_time", timeString || "00:00:00")
                  }
                  placeholder="Hold min"
                />
              </Form.Item>
              <Form.Item label="Hold Max Time" style={{ marginBottom: 10 }}>
                <TimePicker
                  style={{ width: "100%" }}
                  format="HH:mm:ss"
                  value={values.hold_max_time ? dayjs(values.hold_max_time, "HH:mm:ss") : null}
                  onChange={(time, timeString) =>
                    setFieldValue("hold_max_time", timeString || "00:00:00")
                  }
                  placeholder="Hold max"
                />
              </Form.Item>
            </div>
          )}

          {/* ── Footer ── */}
          <Divider style={{ margin: '8px 0' }} />
          <div className="flex justify-end items-center gap-2">
            <Button onClick={() => { resetForm(); onCancel(); }}>
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              className="text-white font-medium"
              style={{ backgroundColor: buttonBgColor }}
            >
              {isEditMode ? "Update Process" : "Create Process"}
            </Button>
          </div>
        </Form>
        );
      }}
    </Formik>
  );
};

export default ProcessForm;
